import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { IndicationInput, IndicationOutput } from '@zg/shared';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { StatusChip } from '../components/StatusChip';
import { RangeHeadline } from '../components/results/RangeHeadline';
import { ScenarioBand } from '../components/results/ScenarioBand';
import { CoverageAccordion } from '../components/results/CoverageAccordion';
import { PermanentSection } from '../components/results/PermanentSection';
import { DisclaimerBlock } from '../components/results/DisclaimerBlock';
import { formatCurrency } from '../lib/format';

interface StoredResult {
  submissionId: string;
  email: string;
  createdAt: number;
  input: IndicationInput;
  output: IndicationOutput;
}

const STORAGE_PREFIX = 'zg-indication-result-';

function loadResult(id: string): StoredResult | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    if (!raw) return null;
    return JSON.parse(raw) as StoredResult;
  } catch {
    return null;
  }
}

export function Result() {
  const { id } = useParams();
  const [record, setRecord] = useState<StoredResult | null | undefined>(undefined);

  useEffect(() => {
    if (!id) {
      setRecord(null);
      return;
    }
    setRecord(loadResult(id));
  }, [id]);

  if (record === undefined) {
    // Loading flash; render nothing substantive.
    return (
      <>
        <Navbar />
        <main className="container-site py-24" aria-busy="true">
          <Eyebrow>Your Indication</Eyebrow>
        </main>
        <Footer />
      </>
    );
  }

  if (record === null) {
    return (
      <>
        <Navbar />
        <main className="container-site py-24" style={{ minHeight: '60vh' }}>
          <Eyebrow>Not Found</Eyebrow>
          <h1
            className="mt-4 font-display font-medium"
            style={{ fontSize: 'clamp(40px,6vw,72px)', lineHeight: 1.1 }}
          >
            This result expired or was not found.
          </h1>
          <p className="mt-6 max-w-2xl text-[1rem] text-[color:var(--color-ink)]/75">
            Indications are stored locally in your browser. If you cleared your storage or opened
            this link on a different device, you&apos;ll need to run the tool again.
          </p>
          <div className="mt-8">
            <Link
              to="/indication"
              className="eyebrow underline underline-offset-4"
              style={{ fontSize: '0.8125rem' }}
            >
              Get an indication
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const { output } = record;
  const lo = output.scenarios.best.total;
  const hi = output.scenarios.conservative.total;
  const varianceDelta = output.variance.bestToConservative.delta;
  const variancePct = output.variance.bestToConservative.pct;

  return (
    <>
      <div className="print-hide">
        <Navbar />
      </div>
      <main>
        {/* Telemetry header + headline + range */}
        <section className="container-site pt-12 pb-16 md:pt-16 md:pb-20">
          <div className="flex items-center gap-3 flex-wrap mb-10">
            <StatusChip label="INDICATION_READY" tone="done" />
            <StatusChip label={`SEQ_${record.submissionId.slice(0, 10)}`} />
          </div>
          <RangeHeadline low={lo} high={hi} />
          <div className="mt-16 max-w-5xl">
            <ScenarioBand
              best={output.scenarios.best}
              average={output.scenarios.average}
              conservative={output.scenarios.conservative}
            />
          </div>
        </section>

        {/* Coverage breakdown */}
        <section className="container-site py-12 md:py-16 border-t border-hairline">
          <Eyebrow>COVERAGE_BREAKDOWN</Eyebrow>
          <h2 className="mt-4 font-display font-medium tracking-[-0.015em] leading-[1.15] text-[clamp(28px,4vw,44px)] max-w-3xl">
            What goes into the range.
          </h2>
          <p className="mt-4 max-w-2xl text-[1rem] leading-relaxed text-ink-muted">
            Tap each coverage to see how Best, Average, and Conservative break down across term
            premium, TRIA, and taxes.
          </p>
          <div className="mt-8 max-w-4xl">
            <CoverageAccordion output={output} />
          </div>
        </section>

        {/* Permanent section */}
        <div className="border-t border-hairline">
          <PermanentSection permanent={output.permanent} />
        </div>

        {/* Variance + disclaimer + next steps */}
        <section className="container-site py-16 md:py-20 border-t border-hairline">
          <div className="max-w-3xl">
            <Eyebrow>SPREAD</Eyebrow>
            <p className="mt-4 font-display font-medium tracking-[-0.01em] leading-[1.25] text-[clamp(22px,2.8vw,30px)]">
              Your conservative estimate is{' '}
              <span className="font-tnum">{formatCurrency(varianceDelta)}</span> more than
              best-case. A {Math.round(variancePct * 100)}% spread.
            </p>

            <div className="mt-10">
              <DisclaimerBlock />
            </div>

            <div className="mt-14 print-hide">
              <Eyebrow>WHAT_HAPPENS_NEXT</Eyebrow>
              <p className="mt-4 max-w-2xl text-[1rem] leading-relaxed text-ink-muted">
                Derek from The Zachary Group will follow up shortly. In the meantime, save this
                page or print it for your records.
              </p>
            </div>
          </div>
        </section>

        {/* Quiet CTA */}
        <div className="print-hide container-site pb-20">
          <div className="relative bg-panel border border-hairline rounded-md p-8 md:p-10 flex flex-col gap-4 items-start max-w-3xl overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-primary"
            />
            <Eyebrow>NEXT_STEP · FORMAL_QUOTE</Eyebrow>
            <h3 className="font-display font-medium tracking-[-0.01em] text-[1.5rem]">
              Want the full indication?
            </h3>
            <a
              className="font-display text-blue-deep underline underline-offset-4 decoration-1 hover:decoration-2 transition-all"
              href="mailto:derek@zacharycompanies.com?subject=Formal%20indication%20request"
            >
              Email our broker
            </a>
          </div>
        </div>
      </main>
      <div className="print-hide">
        <Footer />
      </div>
    </>
  );
}
